/**
 * Input Screen V2 Mappings
 *
 * Mappings for input-type screens:
 * - TextInput: textField placeholder and appearance
 * - WheelPicker: roller label appearance
 *
 * @module themes/mappers/mappingTablesV2
 */

import type { ThemeV2Mapping } from '../ThemeV2Mapper';
import type { StepKind } from '@segmently/schema-models/v2';

// ============================================================================
// StepKind Helpers
// ============================================================================

const isTextInput = (stepKind: StepKind) => stepKind === 'TextInput';
const isWheelPicker = (stepKind: StepKind) => stepKind === 'WheelPicker';

// ============================================================================
// Input Mappings
// ============================================================================

/**
 * Input screen mappings.
 *
 * TextInput V2 paths:
 * - Placeholder font: textField.placeholder.appearance.fontFamily
 * - TextField background: textField.appearance.backgroundColor (uses buttonsBackground)
 * - Entered text color: textField.appearance.enteredTextColor
 *
 * WheelPicker V2 paths:
 * - Label font: roller.labelAppearance.fontFamily
 * - Label color: roller.labelAppearance.color
 */
export const INPUT_V2_MAPPINGS: ThemeV2Mapping[] = [
  // === TextInput ===
  // Placeholder font
  {
    source: 'fonts.main.fontFamily',
    target: 'textField.placeholder.appearance.fontFamily',
    condition: isTextInput,
  },
  // Placeholder color
  {
    source: 'colors.titlesSubtitles',
    target: 'textField.placeholder.appearance.color',
    condition: isTextInput,
  },
  // Entered text color = titlesSubtitles
  {
    source: 'colors.titlesSubtitles',
    target: 'textField.appearance.enteredTextColor',
    condition: isTextInput,
  },
  // Field inner spacing, currently backed by TextFieldStyle.borderMargins.
  {
    source: 'layoutSettings.form.fieldInsets',
    target: 'textField.appearance.borderMargins',
    condition: isTextInput,
  },

  // === WheelPicker ===
  // Roller label font
  {
    source: 'fonts.main.fontFamily',
    target: 'roller.labelAppearance.fontFamily',
    condition: isWheelPicker,
  },
  // Roller label color
  {
    source: 'colors.titlesSubtitles',
    target: 'roller.labelAppearance.color',
    condition: isWheelPicker,
  },
  {
    source: 'layoutSettings.picker.containerRadius',
    target: 'roller.appearance.borders.radius.uniform',
    condition: isWheelPicker,
  },
  {
    source: 'layoutSettings.picker.containerInsets',
    target: 'roller.appearance.insets',
    condition: isWheelPicker,
  },
];
